import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// @mui
import {
  Card,
  Table,
  Stack,
  Popover,
  MenuItem,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
// components
import { pageDataCaptures, deleteMultipleDataCaptures } from '../services/dataCaptureService';
import useAuth from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
// sections
import { SessionTableHead, SessionListToolbar, SessionTableBody } from '../sections/@dashboard/sessions';
// mock

import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'description', label: 'Description', alignRight: false },
  { id: 'startTime', label: 'Start Time', alignRight: false },
  { id: 'lastModified', label: 'Last Modified', alignRight: true },
];

// ----------------------------------------------------------------------

export default function SessionListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [open, setOpen] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('lastModified');
  const [filterName, setFilterName] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [hasSharedSessions, setHasSharedSessions] = useState(false);

  // Debounce the search box -> debouncedFilter, resetting to page 0 on change.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFilter(filterName);
      setPage(0);
    }, 1000);
    return () => clearTimeout(t);
  }, [filterName]);

  // One-shot lightweight probe: only show the sharing tabs if the user has
  // at least one shared recording. Does not eagerly fetch the shared list.
  useEffect(() => {
    let cancelled = false;
    async function probeSharedSessions() {
      try {
        const data = await pageDataCaptures({
          tab: 'shared',
          query: '',
          sort: 'lastModified',
          dir: 'desc',
          page: 0,
          size: 1,
        });
        if (!cancelled) {
          setHasSharedSessions((data.total || 0) > 0);
        }
        // eslint-disable-next-line no-unused-vars
      } catch (_) {
        if (!cancelled) {
          setHasSharedSessions(false);
        }
      }
    }
    probeSharedSessions();
    return () => {
      cancelled = true;
    };
  }, []);

  // Single source of truth: refetch the current page whenever any query input changes.
  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const data = await pageDataCaptures({
        tab: activeTab,
        query: debouncedFilter,
        sort: orderBy,
        dir: order,
        page,
        size: rowsPerPage,
      });
      setSessions(data.items || []);
      setTotal(data.total || 0);
      // eslint-disable-next-line no-unused-vars
    } catch (_) {
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [activeTab, debouncedFilter, orderBy, order, page, rowsPerPage, navigate]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchPage();
    } finally {
      setRefreshing(false);
    }
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      // The rows carry recording ids (SessionTableBody hands handleClick an id,
      // and handleConfirmDelete passes `selected` straight to the delete API),
      // so select-all has to collect ids from the page it is selecting.
      setSelected(sessions.map((session) => session.id));
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value); // debounce effect handles the rest
  };

  const handleDelete = async () => {
    setConfirmDialogOpen(true);
    handleCloseMenu();
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteMultipleDataCaptures(selected);
      setSelected([]);
      const data = await pageDataCaptures({
        tab: activeTab,
        query: debouncedFilter,
        sort: orderBy,
        dir: order,
        page,
        size: rowsPerPage,
      });
      const newTotal = data.total || 0;
      const lastPage = Math.max(0, Math.ceil(newTotal / rowsPerPage) - 1);
      if (page > lastPage) {
        // Current page no longer exists post-delete (e.g. it was the last
        // page and got emptied). Clamp to the last valid page; the existing
        // fetch effect (keyed on `page`) will refetch the correct data.
        // Avoid also setting sessions/total here to prevent a flash of the
        // now-empty page before the refetch completes.
        setPage(lastPage);
      } else {
        setSessions(data.items || []);
        setTotal(newTotal);
      }
      toast.success('Recordings deleted successfully');
    } catch (error) {
      toast.error('Failed to delete recordings');
    }
    setConfirmDialogOpen(false);
  };

  const emptyRows = Math.max(0, rowsPerPage - sessions.length);

  if (loading) return <LoadingSpinner message="Loading recordings..." />;
  return (
    <>
      <Helmet>
        <title> VPSI | Recordings </title>
      </Helmet>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Recordings
          </Typography>
          <IconButton color="primary" onClick={handleRefresh} disabled={refreshing} title="Refresh Recordings">
            <Iconify icon="eva:refresh-fill" />
          </IconButton>
        </Stack>
        {hasSharedSessions && (
          <Tabs
            value={activeTab}
            onChange={(e, val) => {
              setActiveTab(val);
              setPage(0);
            }}
            sx={{ mb: 2 }}
          >
            <Tab label="All" value="all" />
            <Tab label="My Recordings" value="mine" />
            <Tab label="Shared with Me" value="shared" />
          </Tabs>
        )}
        <Card>
          <SessionListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            onDelete={handleDelete}
          />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <SessionTableHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={sessions.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <SessionTableBody
                  sessions={sessions}
                  handleClick={handleClick}
                  selected={selected}
                  emptyRows={emptyRows}
                  user={user}
                />
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }} onClick={handleDelete}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {selected.length} selected recording{selected.length !== 1 ? 's' : ''}? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </>
  );
}
